import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Factory, Play, CheckCircle, Pause, ArrowClockwise, Barcode, SignOut, Lock, Warning, Buildings, X, ArrowsClockwise, UserCircle, ArrowCircleDown } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { mfgApi } from './mfgApi';
import CompleteCapture from './CompleteCapture';
import IssueStockDialog from './IssueStockDialog';
import { WorkInstructionViewer } from './WorkInstructionViewer';
import ItemPickerField from './ItemPickerField';
import { STATUS_COLOR } from './ProductionGantt';
import { useT } from '@/i18n/LanguageContext';

const DEPT_KEY = 'mfg_active_department'; // UI preference only — no sensitive data
const ACTIONABLE = ['released', 'in_progress', 'on_hold'];

const readDept = () => { try { const v = localStorage.getItem(DEPT_KEY); return v && v !== 'all' ? v : null; } catch { return null; } };

export default function MfgKiosk() {
  const { t } = useT();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [deptId, setDeptId] = useState(readDept);
  const [orders, setOrders] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selected, setSelected] = useState(null);
  // 2026-11-05 · 作業者はセッションに1回だけ選ぶ（全操作に記録する）
  const [operator, setOperator] = useState(null);
  const [operators, setOperators] = useState([]);
  const [workView, setWorkView] = useState(null);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanVal, setScanVal] = useState('');
  const [confirm, setConfirm] = useState(null); // 'exit' | 'change'
  // A2: キオスクからの出庫 — 製造オーダーを参照先に固定し、品目だけ選ばせる
  const [issueFor, setIssueFor] = useState(null);
  const [issueItem, setIssueItem] = useState(null);
  const scanRef = useRef(null);

  useEffect(() => { mfgApi.departments().then((d) => setDepartments(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    if (!deptId) return;
    mfgApi.workOrders().then((d) => setOrders((Array.isArray(d) ? d : []).filter((w) => ACTIONABLE.includes(w.status)))).catch(() => {});
    mfgApi.workCenters().then((d) => setCenters(Array.isArray(d) ? d : [])).catch(() => {});
  }, [deptId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    mfgApi.employees().then((rows) => setOperators((Array.isArray(rows) ? rows : [])
      .filter((e) => e.is_active !== false).slice(0, 12)
      .map((e) => ({ id: e.id, name: e.name })))).catch(() => setOperators([]));
  }, []);
  useEffect(() => {
    if (!deptId) return undefined;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [deptId, load]);

  const pickDept = (id) => { try { localStorage.setItem(DEPT_KEY, id); } catch { /* ignore */ } setDeptId(id); };
  const dept = departments.find((d) => d.id === deptId);

  const act = async (fn, okMsg) => {
    if (!selected) return;
    setBusy(true);
    try { await fn(selected.id); toast.success(okMsg); setSelected(null); setConfirmComplete(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };

  const completeKiosk = async (payload) => {
    if (!selected) return;
    setBusy(true);
    try { await mfgApi.completeWorkOrder(selected.id, payload); toast.success(t('mfg.kiosk.doneComplete')); setSelected(null); setConfirmComplete(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };

  const runScan = () => {
    const code = scanVal.trim().toLowerCase();
    if (!code) return;
    const hit = orders.find((w) => (w.manufacturing_no || '').toLowerCase() === code || (w.wo_no || '').toLowerCase() === code);
    setScanOpen(false); setScanVal('');
    if (hit) setSelected(hit);
    else toast.error(t('mfg.kiosk.scanNotFound'));
  };

  // ---- department pre-select screen ----
  if (!deptId) {
    return (
      <div className="fixed inset-0 bg-zinc-950 text-white flex flex-col items-center justify-center p-6" data-testid="kiosk-dept-picker">
        <button onClick={() => navigate('/mfg')} className="absolute top-5 right-5 text-zinc-400 hover:text-white flex items-center gap-1.5 text-sm" data-testid="kiosk-picker-exit"><X size={20} /> {t('mfg.kiosk.exit')}</button>
        <Factory size={52} weight="duotone" className="text-orange-500 mb-3" />
        <h1 className="text-3xl font-bold mb-1">{t('mfg.kiosk.title')}</h1>
        <p className="text-zinc-400 mb-8">{t('mfg.kiosk.pickDept')}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full max-w-3xl">
          {departments.map((d) => (
            <button key={d.id} onClick={() => pickDept(d.id)} data-testid={`kiosk-dept-option-${d.id}`}
              className="flex flex-col items-center gap-2 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-orange-500 active:scale-95 transition-all p-7">
              <span className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: (d.color || '#F97316') + '22' }}>
                <Buildings size={26} weight="duotone" style={{ color: d.color || '#F97316' }} />
              </span>
              <span className="text-lg font-semibold">{d.name}</span>
              {d.code && <span className="text-xs font-mono text-zinc-500">{d.code}</span>}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---- kiosk board ----
  const lanes = [...centers.map((c) => ({ id: c.id, name: c.name || c.code, code: c.code, operators: c.operator_names || [] })), { id: '__none__', name: t('mfg.sched.unassigned'), code: '', operators: [] }]
    .map((lane) => ({ ...lane, orders: orders.filter((o) => (o.work_center_id || '__none__') === lane.id) }))
    .filter((lane) => lane.id !== '__none__' || lane.orders.length > 0);

  return (
    <div className="fixed inset-0 bg-zinc-950 text-white flex flex-col" data-testid="kiosk-root">
      {/* top bar */}
      <div className="flex items-center gap-3 px-5 py-3 bg-zinc-900 border-b border-zinc-800 shrink-0">
        <Factory size={26} weight="duotone" className="text-orange-500" />
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold" data-testid="kiosk-dept-name">{dept?.name || '—'}</span>
          <Lock size={15} className="text-zinc-500" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load} data-testid="kiosk-refresh-btn" className="flex items-center gap-1.5 px-4 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm font-medium"><ArrowsClockwise size={18} /> {t('mfg.kiosk.refresh')}</button>
          <button onClick={() => { setScanOpen(true); setTimeout(() => scanRef.current?.focus(), 200); }} data-testid="kiosk-scan-btn" className="flex items-center gap-1.5 px-4 h-11 rounded-xl bg-blue-600 hover:bg-blue-500 text-sm font-medium"><Barcode size={18} /> {t('mfg.kiosk.scan')}</button>
          <button onClick={() => setConfirm('change')} data-testid="kiosk-change-dept-btn" className="flex items-center gap-1.5 px-4 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm font-medium"><Buildings size={18} /> {t('mfg.kiosk.changeDept')}</button>
          <button onClick={() => setConfirm('exit')} data-testid="kiosk-exit-btn" className="flex items-center gap-1.5 px-4 h-11 rounded-xl bg-zinc-800 hover:bg-red-600 text-sm font-medium"><SignOut size={18} /> {t('mfg.kiosk.exit')}</button>
        </div>
      </div>

      {/* board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden p-4">
        {orders.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-zinc-500" data-testid="kiosk-empty">
            <CheckCircle size={48} weight="duotone" className="mb-2 text-zinc-700" />
            <p className="text-lg">{t('mfg.kiosk.empty')}</p>
          </div>
        ) : (
          <div className="flex gap-4 h-full min-w-max">
            {lanes.map((lane) => (
              <div key={lane.id} className="w-80 shrink-0 flex flex-col bg-zinc-900/60 rounded-2xl border border-zinc-800 overflow-hidden" data-testid={`kiosk-lane-${lane.id}`}>
                <div className="px-4 py-3 border-b border-zinc-800">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{lane.name}</span>
                    <span className="text-xs bg-zinc-800 text-zinc-300 rounded-full px-2 py-0.5">{lane.orders.length}</span>
                  </div>
                  {lane.operators.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`kiosk-lane-operators-${lane.id}`}>
                      {lane.operators.map((n, i) => (
                        <span key={`${n}-${i}`} className="inline-flex items-center gap-1 text-[11px] text-orange-300/90 bg-orange-500/10 border border-orange-500/20 rounded-full px-2 py-0.5">
                          <UserCircle size={12} weight="fill" /> {n}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {lane.orders.map((w) => (
                    <button key={w.id} onClick={() => { setSelected(w); setConfirmComplete(false); }} data-testid={`kiosk-wo-card-${w.id}`}
                      className="w-full text-left rounded-xl bg-zinc-800/80 hover:bg-zinc-800 active:scale-[0.98] transition-all p-4 border-l-4" style={{ borderColor: STATUS_COLOR[w.status] || '#6366f1' }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono font-bold text-lg">{w.manufacturing_no || w.wo_no}</span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold text-white" style={{ backgroundColor: STATUS_COLOR[w.status] }}>{w.status_label}</span>
                      </div>
                      <p className="text-base font-medium mt-1 truncate"><span data-no-translate>{w.product_name}</span></p>
                      <div className="flex items-center gap-3 mt-2 text-sm text-zinc-400">
                        <span className="tabular-nums text-zinc-200">{w.qty} {w.product_uom || ''}</span>
                        {w.due_date && <span className={w.overdue ? 'text-rose-400 font-semibold' : ''}>{t('mfg.wo.dueDate')}: {w.due_date}</span>}
                        {w.has_shortage && <span className="flex items-center gap-1 text-amber-400"><Warning size={14} weight="fill" /> {t('mfg.kiosk.shortage')}</span>}
                      </div>
                    </button>
                  ))}
                  {lane.orders.length === 0 && <p className="text-center text-xs text-zinc-600 py-6">—</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {operator && (
        <div className="absolute right-4 top-3 z-20 flex items-center gap-2 text-sm text-zinc-200"
          data-testid="kiosk-operator-chip">
          <span>作業者：{operator.name}</span>
          <button onClick={() => setOperator(null)} data-testid="kiosk-operator-change"
            className="rounded-full border border-zinc-600 px-2 py-0.5 text-xs">交代</button>
        </div>
      )}

      {/* 作業ビュー（手順 + 品質チェック + 結果を記録） */}
      <WorkInstructionViewer wo={workView} operator={operator} open={!!workView}
        onOpenChange={(o) => { if (!o) setWorkView(null); }}
        onChanged={() => { load(); }} />

      {/* 作業者を選ぶ（セッションに1回） */}
      <Dialog open={!operator} onOpenChange={() => {}}>
        <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="kiosk-operator-dialog">
          <DialogHeader><DialogTitle>作業者を選ぶ</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-1">
            {operators.map((o) => (
              <button key={o.id} onClick={() => setOperator(o)} data-testid={`kiosk-operator-${o.id}`}
                className="h-16 rounded-xl bg-zinc-800 text-lg font-bold text-white hover:bg-zinc-700">
                {o.name}
              </button>
            ))}
            {operators.length === 0 && (
              <button onClick={() => setOperator({ id: null, name: '現場' })}
                data-testid="kiosk-operator-fallback"
                className="col-span-2 h-16 rounded-xl bg-zinc-800 text-lg font-bold text-white">
                名簿が無いので「現場」で続ける
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* action sheet */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setConfirmComplete(false); } }}>
        <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="kiosk-action-sheet">
          {selected && (<>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="font-mono text-xl">{selected.manufacturing_no || selected.wo_no}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold text-white" style={{ backgroundColor: STATUS_COLOR[selected.status] }}>{selected.status_label}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-1 pb-2">
              <p className="text-lg font-bold text-zinc-900"><span data-no-translate>{selected.product_name}</span></p>
              <p className="text-sm text-zinc-500">{selected.product_sku} ・ {selected.qty} {selected.product_uom || ''}{selected.work_center_name ? ` ・ ${selected.work_center_name}` : ''}</p>
              {selected.has_shortage && <p className="flex items-center gap-1.5 text-sm text-amber-600 mt-1"><Warning size={16} weight="fill" /> {t('mfg.kiosk.shortageWarn')}</p>}
            </div>
            {confirmComplete ? (
              <CompleteCapture plannedQty={selected.qty} uom={selected.product_uom} busy={busy}
                onSubmit={completeKiosk} onCancel={() => setConfirmComplete(false)} variant="kiosk" />
            ) : (
              <div className="grid grid-cols-2 gap-3 pt-1">
                {selected.status === 'released' && (
                  <button onClick={() => act(mfgApi.startWorkOrder, t('mfg.kiosk.doneStart'))} disabled={busy} data-testid="kiosk-start-btn" className="col-span-2 h-16 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xl flex items-center justify-center gap-2"><Play size={24} weight="fill" /> {t('mfg.kiosk.start')}</button>
                )}
                {selected.status === 'in_progress' && (
                  <button onClick={() => setConfirmComplete(true)} disabled={busy} data-testid="kiosk-complete-btn" className="col-span-2 h-16 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xl flex items-center justify-center gap-2"><CheckCircle size={24} weight="fill" /> {t('mfg.kiosk.complete')}</button>
                )}
                <button onClick={() => { setWorkView(selected); setSelected(null); }} data-testid="kiosk-work-btn"
                  className="col-span-2 h-16 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xl flex items-center justify-center gap-2">
                  作業を始める
                </button>
                <button onClick={() => setIssueFor(selected)} disabled={busy} data-testid="kiosk-issue-btn"
                  className="col-span-2 h-14 rounded-xl bg-rose-700 hover:bg-rose-600 text-white font-semibold text-lg flex items-center justify-center gap-2">
                  <ArrowCircleDown size={22} weight="fill" /> {t('mfg.action.issue')}
                </button>
                {selected.status === 'on_hold' ? (                  <button onClick={() => act(mfgApi.resumeWorkOrder, t('mfg.kiosk.doneResume'))} disabled={busy} data-testid="kiosk-resume-btn" className="col-span-2 h-16 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xl flex items-center justify-center gap-2"><Play size={24} weight="fill" /> {t('mfg.kiosk.resume')}</button>
                ) : (
                  <button onClick={() => act((id) => mfgApi.holdWorkOrder(id, { reason: t('mfg.kiosk.heldReason') }), t('mfg.kiosk.doneHold'))} disabled={busy} data-testid="kiosk-hold-btn" className="col-span-2 h-14 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-semibold text-lg flex items-center justify-center gap-2"><Pause size={22} weight="fill" /> {t('mfg.kiosk.hold')}</button>
                )}
              </div>
            )}
          </>)}
        </DialogContent>
      </Dialog>

      {/* scan dialog */}
      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="kiosk-scan-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Barcode size={20} /> {t('mfg.kiosk.scanTitle')}</DialogTitle></DialogHeader>
          <Input ref={scanRef} value={scanVal} onChange={(e) => setScanVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runScan()}
            placeholder={t('mfg.kiosk.scanPlaceholder')} data-testid="kiosk-scan-input" className="h-14 text-lg font-mono" autoFocus />
          <button onClick={runScan} data-testid="kiosk-scan-submit" className="h-12 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold">{t('mfg.kiosk.scanGo')}</button>
        </DialogContent>
      </Dialog>

      {/* exit / change confirm */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="kiosk-confirm-dialog">
          <DialogHeader><DialogTitle>{confirm === 'exit' ? t('mfg.kiosk.exitConfirmTitle') : t('mfg.kiosk.changeConfirmTitle')}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">{confirm === 'exit' ? t('mfg.kiosk.exitConfirm') : t('mfg.kiosk.changeConfirm')}</p>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button onClick={() => setConfirm(null)} className="h-12 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-semibold">{t('mfg.cancel')}</button>
            <button data-testid="kiosk-confirm-yes" onClick={() => { if (confirm === 'exit') navigate('/mfg'); else setDeptId(null); setConfirm(null); }} className="h-12 rounded-xl bg-zinc-900 hover:bg-black text-white font-semibold">{t('mfg.kiosk.confirmYes')}</button>
          </div>
        </DialogContent>
      </Dialog>
      {/* 出庫（A2）— 品目を選んでから数量・理由を入れる。参照先は製造オーダーに固定。 */}
      <Dialog open={!!issueFor && !issueItem} onOpenChange={(o) => !o && setIssueFor(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="kiosk-issue-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowCircleDown size={20} /> {t('mfg.action.issue')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            {issueFor?.manufacturing_no || issueFor?.wo_no} ・ {issueFor?.product_name}
          </p>
          <ItemPickerField value="" onChange={(_id, it) => setIssueItem(it)}
            testid="kiosk-issue-item-picker" placeholderKey="mfg.issue.pickItem" />
        </DialogContent>
      </Dialog>
      <IssueStockDialog open={!!issueItem} item={issueItem}
        lockedRef={issueFor ? {
          ref_type: 'work_order', ref_id: issueFor.id,
          label: `${issueFor.manufacturing_no || issueFor.wo_no} ${issueFor.product_name || ''}`,
        } : null}
        onClose={() => { setIssueItem(null); setIssueFor(null); }}
        onDone={load} />
    </div>
  );
}
